import type { BarChartSeries } from '@mantine/charts'
import { BarChart } from '@mantine/charts'
import { ActionIcon, Center, Group, Paper, Text, Title } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import TabChartWrapper from '@/components/TabChartWrapper'
import { colors } from '@/configs/colors'
import { useGlobalState } from '@/hooks/useGlobalState'
import { formatBillingDate, formatChartDate, formatChartLabel } from '@/utils/analytics'

const subscribersSeries: BarChartSeries[] = [
  {
    name: 'userCount',
    label: 'Users',
    color: colors.chartColors[0],
  },
]

export function SubscribersTab() {
  const { api } = useGlobalState()
  const [backPeriod, setBackPeriod] = useState(0)

  const {
    data: subscribersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['billing', 'subscribers', backPeriod],
    queryFn: () => api.getSubscribersData(backPeriod !== 0 ? { back_period: backPeriod } : undefined),
  })

  const chartData = useMemo(() => {
    if (!subscribersData?.periodStart || !subscribersData?.periodEnd) return []

    const generateDateRange = (start: string, end: string): string[] => {
      const dates: string[] = []
      const [startYear, startMonth, startDay] = start.split('T')[0].split('-').map(Number)
      const [endYear, endMonth, endDay] = end.split('T')[0].split('-').map(Number)
      const currentDate = new Date(startYear, startMonth - 1, startDay)
      const endDate = new Date(endYear, endMonth - 1, endDay)

      while (currentDate <= endDate) {
        const year = currentDate.getFullYear()
        const month = String(currentDate.getMonth() + 1).padStart(2, '0')
        const day = String(currentDate.getDate()).padStart(2, '0')
        dates.push(`${year}-${month}-${day}`)
        currentDate.setDate(currentDate.getDate() + 1)
      }
      return dates
    }

    const dataMap = new Map(
      (subscribersData.subscribers ?? []).map(entry => [entry.day.split('T')[0], entry.userCount]),
    )

    const allDates = generateDateRange(subscribersData.periodStart, subscribersData.periodEnd)

    return allDates.map(date => ({
      date,
      userCount: dataMap.get(date) ?? 0,
    }))
  }, [subscribersData])

  const maxSubscribers = useMemo(() => {
    return Math.max(...chartData.map(d => d.userCount), 0)
  }, [chartData])

  const handlePreviousPeriod = () => {
    setBackPeriod(prev => prev - 1)
  }

  const handleNextPeriod = () => {
    setBackPeriod(prev => prev + 1)
  }

  return (
    <Paper radius="md" p="lg" shadow="sm" px="xl">
      <Group justify="space-between" align="center" mb="xl">
        <Title order={6}>Users per Day</Title>
        <Group gap="xs" align="center">
          <ActionIcon
            variant="subtle"
            onClick={handlePreviousPeriod}
            disabled={!subscribersData?.hasPrevious}
            aria-label="Previous period">
            <IconChevronLeft size={20} />
          </ActionIcon>

          <Text size="sm" c="dimmed" style={{ minWidth: 180, textAlign: 'center' }}>
            {formatBillingDate(subscribersData?.periodStart, true)} —{' '}
            {formatBillingDate(subscribersData?.periodEnd, true)}
          </Text>

          <ActionIcon
            variant="subtle"
            onClick={handleNextPeriod}
            disabled={!subscribersData?.hasNext}
            aria-label="Next period">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
      </Group>
      <TabChartWrapper isActive={true} isLoading={isLoading} error={error}>
        {chartData.length === 0 ? (
          <Center h={400}>
            <Text c="dimmed">No subscriber data available for this period</Text>
          </Center>
        ) : (
          <BarChart
            h={400}
            data={chartData}
            dataKey="date"
            series={subscribersSeries}
            gridProps={{ horizontal: false, vertical: false }}
            xAxisProps={{
              tickFormatter: (date: string) => formatChartDate(date),
              angle: -45,
              textAnchor: 'end',
              interval: 0,
            }}
            yAxisProps={{
              label: { value: 'Users', angle: -90, position: 'insideLeft' },
              domain: [0, maxSubscribers + 1],
            }}
            tooltipProps={{
              labelFormatter: label => formatChartLabel(label, true),
              formatter: (value: number | null) => {
                if (value === null || value === 0) return null
                return [`${value.toLocaleString()} subscribers`, 'Subscribers']
              },
            }}
          />
        )}
      </TabChartWrapper>
    </Paper>
  )
}
